import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock, DollarSign, CalendarDays, CheckCircle2, Send,
  ChevronRight,
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { dashboardApi } from '../services/dashboardApi';
import { LABELS, formatPastDate } from '../constants/dashboard';
import { formatPrice } from '../utils/formatters';

const Activity = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const result = await dashboardApi.fetchAll();
      setData(result);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const events = useMemo(() => {
    if (!data) return [];
    const { offers, appointments, publications, vehicles } = data;
    const items = [];

    (offers || []).forEach(o => {
      const vehicle = o.vehicle || {};
      const status = (o.latest_offer || o).status;
      if (status === 'accepted') {
        items.push({ id: `oa-${o.id}`, icon: CheckCircle2, iconColor: 'text-green-400', iconBg: 'bg-green-500/10', title: 'Offre acceptée', detail: `${vehicle.make || ''} ${vehicle.model || ''} — ${formatPrice(o.amount)}`, date: o.updated_at || o.created_at, linkTo: '/marketplace/offers' });
      } else if (status === 'pending' || status === 'countered') {
        items.push({ id: `op-${o.id}`, icon: DollarSign, iconColor: 'text-green-400', iconBg: 'bg-green-500/10', title: LABELS.activityOfferReceived, detail: `${vehicle.make || ''} ${vehicle.model || ''} — ${formatPrice(o.amount)}`, date: o.created_at, linkTo: '/marketplace/offers' });
      }
    });

    (appointments || []).filter(a => a.status === 'confirmed').forEach(a => {
      const vehicle = a.vehicle || {};
      items.push({ id: `apt-${a.id}`, icon: CalendarDays, iconColor: 'text-blue-400', iconBg: 'bg-blue-500/10', title: LABELS.activityAppointmentConfirmed, detail: `${a.buyer?.name || a.buyer?.display_name || ''} · ${vehicle.make || ''} ${vehicle.model || ''}`, date: a.updated_at || a.created_at, linkTo: '/marketplace/appointments' });
    });

    (publications || []).filter(p => p.status === 'published').forEach(p => {
      items.push({ id: `pub-${p.id}`, icon: Send, iconColor: 'text-carmin', iconBg: 'bg-carmin/10', title: LABELS.activityPublished, detail: p.platform || '', date: p.published_at || p.created_at, linkTo: '/dashboard' });
    });

    (vehicles || []).filter(v => v.stock_status === 'sold').forEach(v => {
      items.push({ id: `sold-${v.id}`, icon: CheckCircle2, iconColor: 'text-green-400', iconBg: 'bg-green-500/10', title: LABELS.activitySaleCompleted, detail: `${v.make} ${v.model}${v.price ? ` — ${formatPrice(v.price)}` : ''}`, date: v.updated_at || v.created_at, linkTo: `/vehicles/${v.id}` });
    });

    items.sort((a, b) => {
      const da = a.date ? new Date(a.date) : new Date(0);
      const db = b.date ? new Date(b.date) : new Date(0);
      return db - da;
    });

    return items;
  }, [data]);

  // Group by day
  const grouped = useMemo(() => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    events.forEach(event => {
      const d = event.date ? new Date(event.date).toDateString() : 'unknown';
      let label;
      if (d === today) label = LABELS.activityToday;
      else if (d === yesterday) label = LABELS.activityYesterday;
      else label = event.date ? new Date(event.date).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' }) : '';

      if (!groups[label]) groups[label] = [];
      groups[label].push(event);
    });

    return Object.entries(groups);
  }, [events]);

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-primary">{LABELS.sectionRecentActivity}</h1>
        <p className="text-sm text-muted mt-1">Historique de vos actions et événements</p>
      </div>

      <div className="bg-dark-card rounded-xl border border-dark-border p-4 sm:p-5">
        {events.length > 0 ? (
          <div className="space-y-6">
            {grouped.map(([label, items]) => (
              <div key={label}>
                <p className="text-[11px] font-medium text-muted uppercase tracking-wider mb-3">{label}</p>
                <div className="space-y-1">
                  {items.map(event => {
                    const EventIcon = event.icon;
                    return (
                      <Link
                        key={event.id}
                        to={event.linkTo}
                        className="flex items-start gap-3 py-2.5 px-3 rounded-lg hover:bg-dark-hover transition-colors group"
                      >
                        <div className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${event.iconBg}`}>
                          <EventIcon className={`h-4 w-4 ${event.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-primary">{event.title}</p>
                          <p className="text-xs text-muted truncate">{event.detail}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[11px] text-muted whitespace-nowrap">
                            {formatPastDate(event.date)}
                          </span>
                          <ChevronRight className="h-3 w-3 text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Clock className="h-10 w-10 text-dimmed mx-auto mb-3" />
            <p className="text-sm font-medium text-primary mb-1">Aucune activité récente</p>
            <p className="text-xs text-muted">Vos actions apparaîtront ici au fur et à mesure</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity;
